import { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone } from "lucide-react";
import { MENU_CATEGORIES as STATIC_CATEGORIES, CONTACT } from "@/data/site";
import useApiCollection from "@/lib/useApiCollection";
import { imageUrl } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

function MenuPhoto({ src, alt, testid, className = "mt-5" }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      data-testid={testid}
      className={`${className} flex justify-center`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="block w-[88%] sm:w-full sm:max-w-md aspect-square object-cover rounded-xl border border-tnt-border"
      />
    </motion.div>
  );
}

// Build a unified category list. Prefer admin-managed categories; fall back to
// the static list so the page never appears empty during migrations.
function useMenuCategories() {
  const { data, loading } = useApiCollection("/api/menu-categories");
  return useMemo(() => {
    const fromApi = (data || [])
      .filter((c) => c.visible !== false)
      .map((c) => ({
        id: c.slug,
        title: c.title,
        subtitle: c.subtitle || "",
        banner: c.image_url || "",
        order: c.order ?? 0,
      }))
      .sort((a, b) => a.order - b.order);
    if (fromApi.length > 0) return { categories: fromApi, loading };
    // Static fallback
    return {
      categories: STATIC_CATEGORIES.map((c, i) => ({
        id: c.id,
        title: c.title,
        subtitle: c.subtitle || "",
        banner: c.heroPhoto || "",
        order: i,
      })),
      loading,
    };
  }, [data, loading]);
}

export default function Menu() {
  const { categories } = useMenuCategories();
  const { data: adminMenuPhotos } = useApiCollection("/api/menu-photos");
  const { data: adminMenuItems } = useApiCollection("/api/menu-items");
  const { data: specials } = useApiCollection("/api/specials");

  const [activeId, setActiveId] = useState("");
  const tabsRef = useRef(null);

  // Keep the active tab valid as categories load / change.
  useEffect(() => {
    if (categories.length === 0) return;
    if (!categories.find((c) => c.id === activeId)) {
      setActiveId(categories[0].id);
    }
  }, [categories, activeId]);

  // Auto-scroll the active pill into view inside the horizontal tab bar.
  useEffect(() => {
    const el = tabsRef.current?.querySelector(`[data-tab-id="${activeId}"]`);
    if (el && tabsRef.current) {
      const tabRect = el.getBoundingClientRect();
      const barRect = tabsRef.current.getBoundingClientRect();
      const offset =
        tabRect.left -
        barRect.left -
        barRect.width / 2 +
        tabRect.width / 2 +
        tabsRef.current.scrollLeft;
      tabsRef.current.scrollTo({ left: offset, behavior: "smooth" });
    }
  }, [activeId]);

  // Static items live on the original MENU_CATEGORIES (only when slugs match).
  // They serve purely as a safety fallback when the DB has no items at all for
  // that category — once any admin item exists, statics are suppressed to
  // avoid duplicates after the migration.
  const adminItemsForActive = useMemo(
    () =>
      adminMenuItems
        .filter((m) => m.category === activeId && m.visible !== false),
    [adminMenuItems, activeId],
  );

  const staticItemsForActive = useMemo(() => {
    if (adminItemsForActive.length > 0) return [];
    const cat = STATIC_CATEGORIES.find((c) => c.id === activeId);
    return cat?.items || [];
  }, [activeId, adminItemsForActive.length]);

  const photosForActive = useMemo(
    () => adminMenuPhotos.filter((p) => p.category === activeId),
    [adminMenuPhotos, activeId],
  );

  const activeCategory = categories.find((c) => c.id === activeId);

  return (
    <div data-testid="menu-page" className="wood-bg bg-tnt-bg">
      {/* Header */}
      <section className="relative pt-20 pb-4 sm:pt-24 sm:pb-6 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-tnt-surface to-tnt-bg" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-tnt-gold/8 rounded-full blur-3xl" />
        <div className="relative mx-auto max-w-3xl text-center">
          <h1 className="font-display text-5xl sm:text-7xl text-tnt-ink tracking-tight">
            THE <span className="gold-text">MENU</span>
          </h1>
          <p className="mt-3 text-tnt-muted max-w-xl mx-auto text-sm sm:text-base">
            Our full kitchen serves from 5 PM until 2 AM.
          </p>
        </div>
      </section>

      {/* Sticky category tab bar */}
      <div
        data-testid="menu-tabs"
        className="sticky top-16 z-30 bg-tnt-bg/95 backdrop-blur supports-[backdrop-filter]:bg-tnt-bg/80 border-b border-tnt-border"
      >
        <div className="mx-auto max-w-3xl px-2 sm:px-4">
          <div
            ref={tabsRef}
            className="flex gap-1.5 overflow-x-auto no-scrollbar py-3"
            role="tablist"
            aria-label="Menu categories"
          >
            {categories.map((c) => {
              const active = c.id === activeId;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  data-tab-id={c.id}
                  data-testid={`menu-tab-${c.id}`}
                  aria-selected={active}
                  onClick={() => setActiveId(c.id)}
                  className={`shrink-0 inline-flex items-center px-4 py-2 rounded-full font-display tracking-widest uppercase text-[11px] sm:text-xs transition-colors border ${
                    active
                      ? "bg-tnt-ink text-tnt-bg border-tnt-ink shadow-sm"
                      : "bg-white text-tnt-ink/70 border-tnt-border hover:text-tnt-ink hover:border-tnt-ink/40"
                  }`}
                >
                  {c.title}
                </button>
              );
            })}
            {/* Trailing Specials pseudo-tab */}
            {specials.length > 0 && (
              <button
                type="button"
                role="tab"
                data-tab-id="__specials"
                data-testid="menu-tab-specials"
                aria-selected={activeId === "__specials"}
                onClick={() => setActiveId("__specials")}
                className={`shrink-0 inline-flex items-center px-4 py-2 rounded-full font-display tracking-widest uppercase text-[11px] sm:text-xs transition-colors border ${
                  activeId === "__specials"
                    ? "bg-tnt-gold text-black border-tnt-gold shadow-sm"
                    : "bg-white text-tnt-gold border-tnt-gold/40 hover:border-tnt-gold"
                }`}
              >
                ★ Specials
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Active tab content */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-12 sm:pb-14">
        <div className="mx-auto max-w-3xl flex flex-col">
          <AnimatePresence mode="wait">
            {activeId === "__specials" ? (
              <SpecialsSection key="specials" specials={specials} />
            ) : (
              activeCategory && (
                <motion.section
                  key={activeCategory.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  data-testid={`menu-category-${activeCategory.id}`}
                >
                  {/* Banner photo */}
                  {activeCategory.banner && (
                    <div
                      data-testid={`menu-category-banner-${activeCategory.id}`}
                      className="mb-6 sm:mb-8 rounded-2xl overflow-hidden border border-tnt-border shadow-sm"
                    >
                      <img
                        src={imageUrl(activeCategory.banner)}
                        alt={activeCategory.title}
                        loading="lazy"
                        decoding="async"
                        className="block w-full aspect-[16/9] sm:aspect-[21/9] object-cover bg-tnt-surface2"
                      />
                    </div>
                  )}

                  {/* Category header */}
                  <div className="mb-8 sm:mb-10 border-b border-tnt-border pb-4">
                    <h2 className="font-display text-4xl sm:text-5xl text-tnt-ink tracking-tight">
                      {activeCategory.title.toUpperCase()}
                    </h2>
                    {activeCategory.subtitle && (
                      <p className="text-tnt-muted mt-2 italic font-sans text-sm">
                        {activeCategory.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Admin photo grid */}
                  {photosForActive.length > 0 && (
                    <ul
                      data-testid={`menu-category-photos-${activeCategory.id}`}
                      className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-10"
                    >
                      {photosForActive.map((p) => (
                        <li
                          key={p.id}
                          data-testid={`menu-photo-${p.id}`}
                          className="bg-white border border-tnt-border rounded-xl overflow-hidden shadow-sm"
                        >
                          <img
                            src={imageUrl(p.image_url)}
                            alt={p.name}
                            loading="lazy"
                            decoding="async"
                            className="w-full aspect-square object-cover bg-tnt-surface2"
                          />
                          <div className="p-3">
                            <h4 className="font-display text-tnt-ink leading-tight text-base truncate">
                              {p.name}
                            </h4>
                            {p.description && (
                              <p className="text-tnt-muted text-xs mt-1 line-clamp-2">
                                {p.description}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Items */}
                  {(adminItemsForActive.length === 0 &&
                    staticItemsForActive.length === 0 &&
                    photosForActive.length === 0) ? (
                    <EmptyCategoryHint title={activeCategory.title} />
                  ) : (
                    <ul className="space-y-10">
                      {adminItemsForActive.map((mi) => (
                        <li
                          key={mi.id}
                          data-testid={`menu-item-admin-${mi.id}`}
                          className="border-b border-tnt-border pb-8 last:border-b-0"
                        >
                          <div className="flex items-baseline gap-4">
                            <h3 className="font-display text-2xl sm:text-3xl text-tnt-ink tracking-wide flex-1">
                              {mi.name}
                            </h3>
                            {mi.price && (
                              <span className="font-display text-tnt-gold text-xl sm:text-2xl">
                                {mi.price}
                              </span>
                            )}
                          </div>
                          {mi.description && (
                            <p className="mt-2 text-tnt-muted text-base leading-relaxed">
                              {mi.description}
                            </p>
                          )}
                          <MenuPhoto
                            src={imageUrl(mi.image_url)}
                            alt={mi.name}
                            testid={`menu-item-photo-${mi.id}`}
                            className="mt-5"
                          />
                        </li>
                      ))}
                      {staticItemsForActive.map((item) => (
                        <li
                          key={item.name}
                          data-testid={`menu-item-${slug(item.name)}`}
                          className="border-b border-tnt-border pb-8 last:border-b-0"
                        >
                          <div className="flex items-baseline gap-4">
                            <h3 className="font-display text-2xl sm:text-3xl text-tnt-ink tracking-wide flex-1">
                              {item.name}
                            </h3>
                            <span
                              aria-hidden
                              className="flex-1 border-b border-dotted border-tnt-border/80 mx-2 mb-1.5 hidden sm:block"
                            />
                            <span className="font-display text-2xl sm:text-3xl text-tnt-gold shrink-0">
                              ${item.price}
                            </span>
                          </div>
                          <p className="text-tnt-muted mt-2 text-sm sm:text-base leading-relaxed max-w-2xl">
                            {item.desc}
                          </p>
                          <MenuPhoto
                            src={item.photo}
                            alt={item.name}
                            testid={`menu-item-photo-${slug(item.name)}`}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.section>
              )
            )}
          </AnimatePresence>

          {/* Bottom CTA */}
          <div className="text-center mt-12 sm:mt-16">
            <div className="divider-glow mb-6" />
            <h3 className="font-display text-4xl sm:text-5xl text-tnt-ink tracking-tight">
              HUNGRY YET? <span className="gold-text">COME ON DOWN.</span>
            </h3>
            <p className="text-tnt-muted mt-3 max-w-xl mx-auto">
              Dine in, take out, or call ahead for pickup orders. For groups of
              six or more, give us a call and we&apos;ll save you the best seat
              in the house.
            </p>
            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="menu-call-cta"
              className="btn-gold mt-5"
            >
              <Phone className="w-4 h-4 mr-2" /> Call to Order or Reserve
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecialsSection({ specials }) {
  return (
    <motion.section
      key="__specials"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      data-testid="menu-specials"
    >
      <div className="mb-8 sm:mb-10 border-b border-tnt-border pb-4">
        <h2 className="font-display text-4xl sm:text-5xl text-tnt-ink tracking-tight">
          <span className="gold-text">SPECIALS</span>
        </h2>
        <p className="text-tnt-muted mt-2 italic font-sans text-sm">
          Rotating food, drink & daily deals.
        </p>
      </div>
      <ul className="space-y-6">
        {specials.map((s) => (
          <li
            key={s.id}
            data-testid={`special-${s.id}`}
            className="border-b border-tnt-border pb-5 last:border-b-0"
          >
            <div className="flex items-baseline gap-4">
              <h3 className="font-display text-xl sm:text-2xl text-tnt-ink tracking-wide flex-1">
                {s.title}
              </h3>
              {s.price && (
                <span className="font-display text-tnt-gold text-lg sm:text-xl">
                  {s.price}
                </span>
              )}
            </div>
            <p className="font-display text-tnt-gold text-[10px] tracking-[0.3em] uppercase mt-1">
              {s.kind}
              {s.day_of_week && ` · ${s.day_of_week}`}
            </p>
            {s.description && (
              <p className="mt-2 text-tnt-muted text-sm sm:text-base leading-relaxed">
                {s.description}
              </p>
            )}
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

function EmptyCategoryHint({ title }) {
  return (
    <div
      data-testid="menu-empty-category"
      className="py-14 px-6 text-center border border-dashed border-tnt-border rounded-xl bg-white/50"
    >
      <h3 className="font-display text-tnt-ink text-lg">
        {title} is coming soon
      </h3>
      <p className="text-tnt-muted text-sm mt-1">
        Add items to this category from the Admin panel and they&apos;ll appear
        here automatically.
      </p>
    </div>
  );
}

function slug(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
